import { el } from '../ui/dom';
import {
  CmsClient,
  type CmsLoginMethod,
  type CmsSchool,
  type CmsStudent,
  type CmsTeacher,
  type ImportPreview,
} from './client';
import {
  button,
  dateTime,
  emptyState,
  errorMessage,
  formValue,
  input,
  openDialog,
  selectField,
  setSelectOptions,
  statusBadge,
} from './ui';
import { createLoginMethodControl } from './loginMethodControl';

type ImportKind = 'students' | 'teachers';

interface ImportField {
  readonly key: string;
  readonly label: string;
  readonly type?: 'email' | 'text';
  readonly options?: readonly { readonly value: string; readonly label: string }[];
}

const LOGIN_METHOD_OPTIONS: readonly { readonly value: CmsLoginMethod; readonly label: string }[] = [
  { value: 'email_password', label: 'Email & password' },
];

function loginMethodLabel(method: CmsLoginMethod): string {
  return LOGIN_METHOD_OPTIONS.find((item) => item.value === method)?.label ?? method;
}

function csvExample(kind: ImportKind, schoolId: string, teacherDomain = ''): string {
  if (kind === 'students') {
    return 'student_id,student_name,group,preferred_language,status,email,login_method\r\nS001,Ada Wong,Group 1,en,active,s1@school.edu.hk,email_password\r\n';
  }
  const domain = teacherDomain || 'school.example.edu.hk';
  return `email,display_name,school_id,status,login_method\r\nteacher@${domain},Teacher Name,${schoolId},active,email_password\r\n`;
}

function loginMethodState(_method: CmsLoginMethod): HTMLElement {
  return statusBadge('email & password');
}

function previewSummary(preview: ImportPreview): HTMLElement {
  return el(
    'div',
    { class: 'cms-import-summary' },
    ...[
      ['Total', preview.totalRows, ''],
      ['Valid', preview.validRows, 'success'],
      ['Errors', preview.invalidRows, 'error'],
      ['Duplicates', preview.duplicateRows, 'warning'],
    ].map(([label, value, modifier]) =>
      el(
        'article',
        { class: modifier ? `cms-import-stat cms-import-stat--${modifier}` : 'cms-import-stat' },
        el('span', { text: String(label) }),
        el('strong', { text: String(value) })
      )
    )
  );
}

function errorTable(preview: ImportPreview): HTMLElement {
  if (!preview.errors.length) {
    return el('div', { class: 'cms-import-ready' }, el('strong', { text: 'Every row is ready to import.' }));
  }
  return el(
    'div',
    { class: 'cms-table-scroll' },
    el(
      'table',
      { class: 'cms-data-table cms-data-table--errors' },
      el('thead', {}, el('tr', {}, ...['Row', 'Column', 'Issue'].map((label) => el('th', { text: label })))),
      el(
        'tbody',
        {},
        ...preview.errors.map((error) =>
          el('tr', {}, el('td', { text: error.row }), el('td', { text: error.field }), el('td', { text: error.message }))
        )
      )
    )
  );
}

function credentialTable(
  credentials: readonly { loginName: string; loginMethod: CmsLoginMethod; temporaryPassword: string; displayName: string; role: string }[]
): HTMLElement {
  return el(
    'div',
    { class: 'cms-table-scroll' },
    el(
      'table',
      { class: 'cms-data-table' },
      el('thead', {}, el('tr', {}, ...['Name', 'Role', 'Login', 'Method', 'Password'].map((label) => el('th', { text: label })))),
      el(
        'tbody',
        {},
        ...credentials.map((credential) =>
          el(
            'tr',
            {},
            el('td', { text: credential.displayName }),
            el('td', { text: credential.role }),
            el('td', { text: credential.loginName }),
            el('td', { text: loginMethodLabel(credential.loginMethod) }),
            el('td', {}, credential.temporaryPassword ? el('code', { text: credential.temporaryPassword }) : el('span', { text: 'Not available' }))
          )
        )
      )
    )
  );
}

const IMPORT_FIELDS: Record<ImportKind, readonly ImportField[]> = {
  students: [
    { key: 'student_id', label: 'Student ID' },
    { key: 'student_name', label: 'Name' },
    { key: 'group', label: 'Group' },
    {
      key: 'preferred_language',
      label: 'Language',
      options: [
        { value: 'zh-HK', label: '繁體中文' },
        { value: 'en', label: 'English' },
      ],
    },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'login_method', label: 'Login method', options: LOGIN_METHOD_OPTIONS },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ],
  teachers: [
    { key: 'display_name', label: 'Name' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'school_id', label: 'School ID' },
    { key: 'login_method', label: 'Login method', options: LOGIN_METHOD_OPTIONS },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ],
};

function csvText(fields: readonly ImportField[], rows: readonly Readonly<Record<string, string>>[]): string {
  const cell = (value: string): string =>
    /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  return `${fields.map((field) => field.key).join(',')}\r\n${rows
    .map((row) => fields.map((field) => cell(row[field.key] ?? '')).join(','))
    .join('\r\n')}\r\n`;
}

function importSteps(active: 1 | 2 | 3): HTMLElement {
  return el(
    'ol',
    { class: 'cms-import-steps', attrs: { 'aria-label': 'Import progress' } },
    ...['Upload & preview', 'Review & confirm', 'Complete'].map((label, index) =>
      el(
        'li',
        { class: index + 1 <= active ? 'cms-import-step cms-import-step--active' : 'cms-import-step' },
        el('span', { text: String(index + 1) }),
        el('strong', { text: label })
      )
    )
  );
}

function editableImportTable(
  kind: ImportKind,
  rows: readonly Readonly<Record<string, string>>[],
  onChange: (index: number, key: string, value: string) => void,
  onRemove: (index: number) => void
): HTMLElement {
  const fields = IMPORT_FIELDS[kind];
  return el(
    'div',
    { class: 'cms-table-scroll cms-import-editable' },
    el(
      'table',
      { class: 'cms-data-table' },
      el(
        'thead',
        {},
        el('tr', {}, ...fields.map((field) => el('th', { text: field.label })), el('th', { text: '' }))
      ),
      el(
        'tbody',
        {},
        ...rows.map((row, index) =>
          el(
            'tr',
            {},
            ...fields.map((field) => {
              const control = field.options
                ? el('select', {
                    class: 'cms-select cms-inline-control',
                    attrs: { 'aria-label': `${field.label} for row ${index + 1}` },
                  })
                : el('input', {
                    class: 'cms-input cms-inline-control',
                    attrs: {
                      type: field.type ?? 'text',
                      value: row[field.key] ?? '',
                      'aria-label': `${field.label} for row ${index + 1}`,
                    },
                  });
              if (control instanceof HTMLSelectElement && field.options) {
                setSelectOptions(control, field.options, row[field.key] ?? '');
              }
              control.addEventListener('change', () => onChange(index, field.key, control.value));
              control.addEventListener('input', () => onChange(index, field.key, control.value));
              return el('td', {}, control);
            }),
            el('td', {}, button('Remove', 'quiet', () => onRemove(index)))
          )
        )
      )
    )
  );
}

function openImport(
  client: CmsClient,
  kind: ImportKind,
  schoolId: string,
  teacherDomain: string,
  onImported: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const title = kind === 'students' ? 'Import student accounts' : 'Import teacher accounts';
  const description =
    kind === 'students'
      ? 'Upload UTF-8 CSV. Valid rows can continue even when other rows have errors.'
      : 'Teachers must already belong to an active school and use one of its active email domains.';
  const modal = openDialog(title, description, true);
  const template = csvExample(kind, schoolId, teacherDomain);

  const complete = (rows: readonly Readonly<Record<string, string>>[]): void => {
    const commitButton = button(`Confirm import ${rows.length}`, 'primary');
    commitButton.addEventListener('click', () => {
      commitButton.disabled = true;
      commitButton.textContent = 'Rechecking…';
      void client
        .previewImport(kind, csvText(IMPORT_FIELDS[kind], rows), schoolId)
        .then((rechecked) => {
          if (rechecked.invalidRows > 0) {
            announce('Some edited rows still need attention.', true);
            renderReview(rechecked);
            return undefined;
          }
          commitButton.textContent = 'Importing…';
          return client.commitImport(kind, rechecked.previewToken).then((result) => {
            client.downloadCsv(result.csv, `${kind}-one-time-credentials.csv`);
            modal.body.replaceChildren(
              importSteps(3),
              el(
                'div',
                { class: 'cms-success-banner' },
                el('strong', { text: `${result.imported} accounts created.` }),
                el('p', {
                  text: 'The credential CSV was downloaded. Passwords are displayed only in this result.',
                })
              ),
              credentialTable(result.credentials),
              el(
                'div',
                { class: 'cms-dialog__actions cms-dialog__actions--end' },
                button('Done', 'primary', modal.close)
              )
            );
            announce(`${result.imported} ${kind} imported.`);
            onImported();
          });
        })
        .catch((reason) => {
          commitButton.disabled = false;
          commitButton.textContent = `Confirm import ${rows.length}`;
          announce(errorMessage(reason), true);
        });
    });
    modal.body.append(
      el(
        'div',
        { class: 'cms-dialog__actions cms-dialog__actions--end' },
        button('Back', 'secondary', renderUpload),
        commitButton
      )
    );
  };

  const renderReview = (preview: ImportPreview): void => {
    const rows: Record<string, string>[] = preview.rows.map((row) => ({ ...row }));
    const renderRows = (): void => {
      const table = editableImportTable(
        kind,
        rows,
        (index, key, value) => {
          rows[index] = { ...rows[index], [key]: value };
        },
        (index) => {
          rows.splice(index, 1);
          renderRows();
        }
      );
      const reviewSection = el(
        'section',
        { class: 'cms-import-review' },
        el('h3', { class: 'cms-subheading', text: `Review ${rows.length} valid account${rows.length === 1 ? '' : 's'}` }),
        el('p', {
          text: 'Edit any value before importing. Every edited row is validated by the server again before an account is created.',
        }),
        ...(rows.length
          ? [table]
          : [emptyState('No valid accounts remain.', 'Go back and choose another CSV file.')])
      );
      modal.body.replaceChildren(
        importSteps(2),
        previewSummary(preview),
        ...(preview.errors.length
          ? [
              el('h3', { class: 'cms-subheading', text: 'Rows that will be skipped' }),
              errorTable(preview),
            ]
          : []),
        reviewSection
      );
      if (rows.length) complete(rows);
      else {
        modal.body.append(
          el(
            'div',
            { class: 'cms-dialog__actions cms-dialog__actions--end' },
            button('Back', 'secondary', renderUpload)
          )
        );
      }
    };
    renderRows();
  };

  function renderUpload(): void {
    const fileInput = el('input', {
      class: 'cms-file-input',
      attrs: { type: 'file', accept: '.csv,text/csv' },
    });
    const previewButton = button('Preview CSV', 'primary');
    previewButton.addEventListener('click', () => {
      const file = fileInput.files?.[0];
      if (!file) {
        announce('Choose a CSV file first.', true);
        return;
      }
      previewButton.disabled = true;
      previewButton.textContent = 'Checking…';
      void file
        .text()
        .then((source) => client.previewImport(kind, source, schoolId))
        .then(renderReview)
        .catch((reason) => {
          previewButton.disabled = false;
          previewButton.textContent = 'Preview CSV';
          announce(errorMessage(reason), true);
        });
    });
    modal.body.replaceChildren(
      importSteps(1),
      el(
        'section',
        { class: 'cms-upload-zone' },
        el('strong', { text: 'Choose a UTF-8 CSV file' }),
        el('p', {
          text:
            kind === 'students'
              ? 'Required: student_id, student_name and group. Authentication is selected per row.'
              : `Every teacher will be checked against the selected school (${schoolId}) and its active email domains.`,
        }),
        fileInput,
        el(
          'div',
          { class: 'cms-dialog__actions' },
          button('Download CSV template', 'secondary', () =>
            client.downloadCsv(template, `${kind}-import-example.csv`)
          ),
          previewButton
        )
      ),
      el(
        'div',
        { class: 'cms-dialog__actions cms-dialog__actions--end' },
        button('Cancel', 'secondary', modal.close)
      )
    );
  }

  renderUpload();
}

function openStudentForm(
  client: CmsClient,
  schoolId: string,
  student: CmsStudent | null,
  canChangePasswords: boolean,
  onSaved: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const modal = openDialog(
    student ? 'Edit student' : 'Add student',
    student
      ? 'Update profile, email and access status.'
      : 'Accounts sign in with email and password.'
  );
  const emailField = input('Email', 'email', 'email', student?.email ?? '');
  const emailInput = emailField.querySelector('input');
  const loginControl = createLoginMethodControl({
    current: student?.loginMethod ?? 'email_password',
  });
  const hint = el('small', { class: 'cms-form-hint cms-auth-method-note' });
  loginControl.element.append(hint);
  const resetPasswordInput = el('input', {
    attrs: { type: 'checkbox', name: 'resetPassword', value: 'yes' },
  });
  const resetPasswordField = el(
    'label',
    { class: 'cms-password-reset-option' },
    resetPasswordInput,
    el(
      'span',
      {},
      el('strong', { text: 'Generate a new password' }),
      el('small', { text: 'The current password stops working after this account is saved.' })
    )
  );
  const syncLoginMethod = (): void => {
    if (!emailInput) return;
    const method = loginControl.value();
    emailInput.required = Boolean(student) || method !== 'email_password';
    resetPasswordField.hidden = !canChangePasswords || !student;
    hint.textContent =
      method === 'email_password'
        ? student
          ? 'This email is the student’s game login.'
          : 'Email is optional; a unique Academy login is generated when blank.'
        : `Use the exact verified ${loginMethodLabel(method)} email. The school domain must be enabled.`;
  };
  loginControl.onChange(syncLoginMethod);
  syncLoginMethod();
  const form = el(
    'form',
    { class: 'cms-form' },
    input('Student ID', 'studentId', 'text', student?.studentId ?? '', true),
    input('Student name', 'studentName', 'text', student?.studentName ?? '', true),
    input('Group', 'groupName', 'text', student?.className ?? '', true),
    emailField,
    loginControl.element,
    resetPasswordField,
    selectField('Language', 'preferredLanguage', [{ value: 'zh-HK', label: '繁體中文' }, { value: 'en', label: 'English' }], student?.preferredLanguage ?? 'zh-HK'),
    selectField('Status', 'status', [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }], student?.status ?? 'active')
  );
  const save = button(student ? 'Save changes' : 'Create account', 'primary');
  modal.body.append(form, el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Cancel', 'secondary', modal.close), save));
  const submit = (): void => {
    if (!form.reportValidity()) return;
    const payload = {
      studentId: formValue(form, 'studentId'),
      studentName: formValue(form, 'studentName'),
      groupName: formValue(form, 'groupName'),
      email: formValue(form, 'email'),
      loginMethod: loginControl.value(),
      resetPassword: canChangePasswords && resetPasswordInput.checked,
      preferredLanguage: formValue(form, 'preferredLanguage'),
      status: formValue(form, 'status'),
    };
    save.disabled = true;
    void (student ? client.updateStudent(student.id, payload) : client.addStudent(payload, schoolId))
      .then((result) => {
        if (result.credential) {
          modal.body.replaceChildren(
            el(
              'div',
              { class: 'cms-success-banner' },
              el('strong', { text: student ? 'Student login updated.' : 'Student account created.' }),
              el('p', {
                text: result.credential.temporaryPassword
                  ? 'Copy these credentials now; the password will not be shown again.'
                  : `The student can sign in with ${loginMethodLabel(result.credential.loginMethod)} using the exact email shown.`,
              })
            ),
            credentialTable([result.credential]),
            el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Done', 'primary', modal.close))
          );
        } else {
          modal.close();
        }
        announce(student ? 'Student updated.' : 'Student created.');
        onSaved();
      })
      .catch((reason) => announce(errorMessage(reason), true))
      .finally(() => {
        save.disabled = false;
      });
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
  save.addEventListener('click', submit);
}

export async function createStudentsView(
  client: CmsClient,
  schoolId: string,
  announce: (message: string, error?: boolean) => void,
  canChangePasswords = false
): Promise<HTMLElement> {
  let students: readonly CmsStudent[] = [];
  let progress = new Map<string, { completed: number; total: number }>();
  const tableBody = el('tbody');
  const searchInput = el('input', { class: 'cms-input', attrs: { type: 'search', placeholder: 'Search students', 'aria-label': 'Search students' } });
  const classSelect = el('select', { class: 'cms-select', attrs: { 'aria-label': 'Group' } });
  const statusSelect = el('select', { class: 'cms-select', attrs: { 'aria-label': 'Status' } });
  setSelectOptions(statusSelect, [{ value: '', label: 'All statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]);

  const render = (): void => {
    const query = searchInput.value.trim().toLowerCase();
    const filtered = students.filter(
      (student) =>
        (!query || `${student.studentName} ${student.studentId} ${student.className} ${student.loginName ?? ''}`.toLowerCase().includes(query)) &&
        (!classSelect.value || student.className === classSelect.value) &&
        (!statusSelect.value || student.status === statusSelect.value)
    );
    tableBody.replaceChildren(
      ...filtered.map((student) => {
        const item = progress.get(student.id) ?? { completed: 0, total: 3 };
        const percentage = item.total ? Math.round((item.completed / item.total) * 100) : 0;
        return el(
          'tr',
          {},
          el('td', {}, el('strong', { text: student.studentName }), el('span', { class: 'cms-row-subtitle', text: student.studentId })),
          el('td', { text: student.className }),
          el(
            'td',
            {},
            el('span', { class: 'cms-login-name', text: student.email || student.loginName || '—' }),
            el('span', { class: 'cms-row-subtitle' }, loginMethodState(student.loginMethod))
          ),
          el('td', {}, statusBadge(student.status)),
          el('td', {}, el('div', { class: 'cms-progress-inline' }, el('span', { text: `${percentage}%` }), el('div', { class: 'cms-progress-bar' }, el('span', { style: { width: `${percentage}%` } })))),
          el(
            'td',
            {},
            el(
              'div',
              { class: 'cms-row-actions' },
              button('Edit', 'quiet', () => openStudentForm(client, schoolId, student, canChangePasswords, () => void refresh(), announce)),
              button(student.status === 'active' ? 'Disable' : 'Enable', 'quiet', () =>
                void client
                  .updateStudent(student.id, { status: student.status === 'active' ? 'inactive' : 'active' })
                  .then(() => refresh())
                  .catch((reason) => announce(errorMessage(reason), true))
              ),
              ...(canChangePasswords
                ? [
                    button('Reset password', 'quiet', () => {
                      void client
                        .resetStudentPassword(student.id)
                        .then((credential) => {
                          const modal = openDialog('Temporary password', 'Copy it now; only the hash is stored after this screen.');
                          modal.body.append(
                            credentialTable([
                              {
                                ...credential,
                                displayName: student.studentName,
                                role: 'student',
                                loginMethod: 'email_password',
                              },
                            ]),
                            el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Done', 'primary', modal.close))
                          );
                          announce('Student password reset.');
                        })
                        .catch((reason) => announce(errorMessage(reason), true));
                    }),
                  ]
                : [])
            )
          )
        );
      })
    );
    if (!filtered.length) tableBody.replaceChildren(el('tr', {}, el('td', { attrs: { colspan: 6 } }, emptyState('No students found.', 'Change the filters or import student accounts.'))));
  };

  const refresh = async (): Promise<void> => {
    announce('Loading students…');
    try {
      const [studentRows, report] = await Promise.all([client.students('', schoolId), client.schoolReport({ schoolId })]);
      students = studentRows;
      progress = new Map(
        report.rows.map((row) => [row.student.id, { completed: Object.values(row.results).filter((result) => result.bestAttempt?.status === 'completed').length, total: report.games.length }])
      );
      const currentClass = classSelect.value;
      setSelectOptions(classSelect, [{ value: '', label: 'All groups' }, ...[...new Set(students.map((student) => student.className))].sort().map((value) => ({ value, label: value }))], currentClass);
      render();
      announce(`${students.length} students loaded.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
      tableBody.replaceChildren(el('tr', {}, el('td', { attrs: { colspan: 6 } }, emptyState('Students unavailable.', errorMessage(reason)))));
    }
  };
  searchInput.addEventListener('input', render);
  classSelect.addEventListener('change', render);
  statusSelect.addEventListener('change', render);

  const view = el(
    'div',
    { class: 'cms-view' },
    el(
      'div',
      { class: 'cms-view-heading' },
      el('div', {}, el('h1', { text: 'Students' }), el('p', { text: 'Create, import, search, update, disable and reset student accounts.' })),
      el(
        'div',
        { class: 'cms-view-actions' },
        button('Add student', 'highlight', () => openStudentForm(client, schoolId, null, canChangePasswords, () => void refresh(), announce)),
        button('Download CSV example', 'secondary', () =>
          client.downloadCsv(csvExample('students', schoolId), 'students-import-example.csv')
        ),
        button('Import CSV', 'primary', () => openImport(client, 'students', schoolId, '', () => void refresh(), announce))
      )
    ),
    el('div', { class: 'cms-filter-bar' }, el('label', { class: 'cms-search-field' }, searchInput), classSelect, statusSelect, button('Refresh', 'secondary', () => void refresh())),
    el('section', { class: 'cms-panel cms-panel--flush' }, el('div', { class: 'cms-table-scroll' }, el('table', { class: 'cms-data-table' }, el('thead', {}, el('tr', {}, ...['Student', 'Group', 'Login', 'Status', 'Progress', 'Actions'].map((label) => el('th', { text: label })))), tableBody)))
  );
  await refresh();
  return view;
}

function openTeacherForm(
  client: CmsClient,
  schools: readonly CmsSchool[],
  selectedSchoolId: string,
  teacher: CmsTeacher | null,
  onSaved: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const modal = openDialog(
    teacher ? 'Edit teacher' : 'Create teacher',
    'Choose the teacher’s school and email. Only System Admins can reset passwords.'
  );
  const activeSchools = schools.filter((school) => school.status === 'active');
  const loginControl = createLoginMethodControl({
    current: 'email_password',
  });
  const resetPasswordInput = el('input', {
    attrs: { type: 'checkbox', name: 'resetPassword', value: 'yes' },
  });
  const resetPasswordField = el(
    'label',
    { class: 'cms-password-reset-option' },
    resetPasswordInput,
    el(
      'span',
      {},
      el('strong', { text: 'Generate a new password' }),
      el('small', { text: 'The current password stops working after this account is saved.' })
    )
  );
  const syncLoginMethod = (): void => {
    resetPasswordField.hidden = !teacher || loginControl.value() !== 'email_password';
  };
  loginControl.onChange(syncLoginMethod);
  syncLoginMethod();
  const form = el(
    'form',
    { class: 'cms-form' },
    input('Display name', 'displayName', 'text', teacher?.displayName ?? '', true),
    input('Email', 'email', 'email', teacher?.email ?? '', true),
    selectField(
      'School',
      'schoolId',
      activeSchools.map((school) => ({ value: school.id, label: `${school.code} · ${school.name}` })),
      teacher?.schoolId ?? selectedSchoolId
    ),
    loginControl.element,
    resetPasswordField,
    selectField(
      'Status',
      'status',
      [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      teacher?.status ?? 'active'
    )
  );
  const save = button(teacher ? 'Save changes' : 'Create teacher', 'primary');
  const submit = (): void => {
    if (!form.reportValidity()) return;
    save.disabled = true;
    const payload = {
      displayName: formValue(form, 'displayName'),
      email: formValue(form, 'email'),
      schoolId: formValue(form, 'schoolId'),
      loginMethod: loginControl.value(),
      resetPassword: resetPasswordInput.checked,
      status: formValue(form, 'status'),
    };
    const request = teacher
      ? client.updateTeacher(teacher.id, payload)
      : client.createTeacher(payload);
    void request
      .then((result) => {
        if (result.credential) {
          modal.body.replaceChildren(
            el(
              'div',
              { class: 'cms-success-banner' },
              el('strong', { text: teacher ? 'Teacher login updated.' : 'Teacher account created.' }),
              el('p', { text: 'Copy the password now; it will not be shown again.' })
            ),
            credentialTable([result.credential]),
            el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Done', 'primary', modal.close))
          );
        } else {
          modal.close();
        }
        announce(teacher ? 'Teacher updated.' : 'Teacher created.');
        onSaved();
      })
      .catch((reason) => announce(errorMessage(reason), true))
      .finally(() => {
        save.disabled = false;
      });
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
  save.addEventListener('click', submit);
  modal.body.append(
    form,
    el(
      'div',
      { class: 'cms-dialog__actions cms-dialog__actions--end' },
      button('Cancel', 'secondary', modal.close),
      save
    )
  );
}

export async function createTeachersView(
  client: CmsClient,
  schoolId: string,
  schools: readonly CmsSchool[],
  announce: (message: string, error?: boolean) => void
): Promise<HTMLElement> {
  let teachers: readonly CmsTeacher[] = [];
  const body = el('tbody');
  const searchInput = el('input', { class: 'cms-input', attrs: { type: 'search', placeholder: 'Search teachers', 'aria-label': 'Search teachers' } });
  const statusSelect = el('select', { class: 'cms-select' });
  setSelectOptions(statusSelect, [{ value: '', label: 'All statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]);
  const render = (): void => {
    const query = searchInput.value.trim().toLowerCase();
    const rows = teachers.filter((teacher) => (!query || `${teacher.displayName} ${teacher.email}`.toLowerCase().includes(query)) && (!statusSelect.value || teacher.status === statusSelect.value));
    body.replaceChildren(
      ...rows.map((teacher) =>
        el(
          'tr',
          {},
          el(
            'td',
            {},
            el('strong', { text: teacher.displayName }),
            el('span', { class: 'cms-row-subtitle', text: teacher.email })
          ),
          el('td', {
            text: schools.find((school) => school.id === teacher.schoolId)?.name ?? teacher.schoolId,
          }),
          el(
            'td',
            {},
            loginMethodState(teacher.loginMethod)
          ),
          el('td', { text: dateTime(teacher.lastLoginAt) }),
          el('td', {}, statusBadge(teacher.status)),
          el(
            'td',
            {},
            el(
              'div',
              { class: 'cms-row-actions' },
              button('Edit', 'quiet', () =>
                openTeacherForm(client, schools, schoolId, teacher, () => void refresh(), announce)
              ),
              button(teacher.status === 'active' ? 'Disable' : 'Enable', 'quiet', () => {
                void client
                  .updateTeacher(teacher.id, {
                    status: teacher.status === 'active' ? 'inactive' : 'active',
                  })
                  .then(() => refresh())
                  .catch((reason) => announce(errorMessage(reason), true));
              })
            )
          )
        )
      )
    );
    if (!rows.length) body.replaceChildren(el('tr', {}, el('td', { attrs: { colspan: 6 } }, emptyState('No teachers found.', 'Change the filters or import teacher accounts.'))));
  };
  const refresh = async (): Promise<void> => {
    try {
      teachers = await client.teachers('', schoolId);
      render();
      announce(`${teachers.length} teachers loaded.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
    }
  };
  searchInput.addEventListener('input', render);
  statusSelect.addEventListener('change', render);
  const selectedSchool = schools.find((school) => school.id === schoolId);
  const teacherDomain = selectedSchool?.domains.find((domain) => domain.status === 'active')?.domain ?? '';
  const view = el(
    'div',
    { class: 'cms-view' },
    el(
      'div',
      { class: 'cms-view-heading' },
      el('div', {}, el('h1', { text: 'Teachers' }), el('p', { text: 'Create, import and manage teacher email, school, login method and access.' })),
      el(
        'div',
        { class: 'cms-view-actions' },
        button('Create teacher', 'highlight', () =>
          openTeacherForm(client, schools, schoolId, null, () => void refresh(), announce)
        ),
        button('Download CSV example', 'secondary', () =>
          client.downloadCsv(
            csvExample('teachers', schoolId, teacherDomain),
            'teachers-import-example.csv'
          )
        ),
        button('Import CSV', 'primary', () =>
          openImport(client, 'teachers', schoolId, teacherDomain, () => void refresh(), announce)
        )
      )
    ),
    el('div', { class: 'cms-filter-bar' }, el('label', { class: 'cms-search-field' }, searchInput), statusSelect, button('Refresh', 'secondary', () => void refresh())),
    el(
      'section',
      { class: 'cms-panel cms-panel--flush' },
      el(
        'div',
        { class: 'cms-table-scroll' },
        el(
          'table',
          { class: 'cms-data-table' },
          el('thead', {}, el('tr', {}, ...['Teacher', 'School', 'Login method', 'Last login', 'Status', 'Actions'].map((label) => el('th', { text: label })))),
          body
        )
      )
    )
  );
  await refresh();
  return view;
}
